/**
 * Unidades de medida do estoque.
 *
 * Um produto tem DUAS unidades, e é essa distinção que o resto do módulo
 * depende: `unidade` é a EMBALAGEM — como o estoque é contado e comprado —, e
 * `unidadeConsumo` é a escala em que o procedimento debita. Um frasco de
 * toxina de 100 UI entra como 1 frasco e sai em UI.
 *
 * O erro que este arquivo existe para impedir é multiplicar o custo da
 * embalagem pela quantidade consumida: 50 UI de um frasco de R$ 890 custam
 * R$ 445, não R$ 44.500.
 */

export interface Unidade {
    id: string;
    /** O que aparece ao lado do número. Sempre no singular. */
    abrev: string;
    /** O que o seletor mostra. */
    nome: string;
    /** Nulo quando a unidade não inflete: "3 ml", nunca "3 mls". */
    plural: string | null;
    grupo: string;
}

/* Na ordem em que a pessoa procura: contagem e embalagem respondem quase todo
   o estoque, e ml/UI só aparecem em injetável. */
export const UNIDADES: Unidade[] = [
    { id: 'und', abrev: 'und', nome: 'Unidade (und)', plural: null, grupo: 'Contagem' },
    { id: 'par', abrev: 'par', nome: 'Par', plural: 'pares', grupo: 'Contagem' },
    { id: 'kit', abrev: 'kit', nome: 'Kit', plural: 'kits', grupo: 'Contagem' },

    { id: 'frasco', abrev: 'frasco', nome: 'Frasco', plural: 'frascos', grupo: 'Embalagem' },
    { id: 'ampola', abrev: 'ampola', nome: 'Ampola', plural: 'ampolas', grupo: 'Embalagem' },
    { id: 'seringa', abrev: 'seringa', nome: 'Seringa', plural: 'seringas', grupo: 'Embalagem' },
    { id: 'carpule', abrev: 'carpule', nome: 'Carpule (tubete)', plural: 'carpules', grupo: 'Embalagem' },
    { id: 'bisnaga', abrev: 'bisnaga', nome: 'Bisnaga', plural: 'bisnagas', grupo: 'Embalagem' },
    { id: 'tubo', abrev: 'tubo', nome: 'Tubo', plural: 'tubos', grupo: 'Embalagem' },
    { id: 'pote', abrev: 'pote', nome: 'Pote', plural: 'potes', grupo: 'Embalagem' },
    { id: 'sache', abrev: 'sachê', nome: 'Sachê', plural: 'sachês', grupo: 'Embalagem' },
    { id: 'envelope', abrev: 'envelope', nome: 'Envelope', plural: 'envelopes', grupo: 'Embalagem' },
    { id: 'caixa', abrev: 'caixa', nome: 'Caixa', plural: 'caixas', grupo: 'Embalagem' },
    { id: 'rolo', abrev: 'rolo', nome: 'Rolo', plural: 'rolos', grupo: 'Embalagem' },

    { id: 'ml', abrev: 'ml', nome: 'Mililitro (ml)', plural: null, grupo: 'Volume' },
    { id: 'ul', abrev: 'µl', nome: 'Microlitro (µl)', plural: null, grupo: 'Volume' },
    { id: 'l', abrev: 'L', nome: 'Litro (L)', plural: null, grupo: 'Volume' },
    { id: 'gts', abrev: 'gts', nome: 'Gotas', plural: null, grupo: 'Volume' },

    { id: 'mg', abrev: 'mg', nome: 'Miligrama (mg)', plural: null, grupo: 'Massa' },
    { id: 'mcg', abrev: 'mcg', nome: 'Micrograma (mcg)', plural: null, grupo: 'Massa' },
    { id: 'g', abrev: 'g', nome: 'Grama (g)', plural: null, grupo: 'Massa' },
    { id: 'ui', abrev: 'UI', nome: 'Unidade internacional (UI)', plural: null, grupo: 'Massa' },

    { id: 'aplicacao', abrev: 'aplicação', nome: 'Aplicação', plural: 'aplicações', grupo: 'Uso clínico' },
    { id: 'sessao', abrev: 'sessão', nome: 'Sessão', plural: 'sessões', grupo: 'Uso clínico' },
    { id: 'cm2', abrev: 'cm²', nome: 'Centímetro quadrado (cm²)', plural: null, grupo: 'Uso clínico' },
    { id: 'metro', abrev: 'm', nome: 'Metro (m)', plural: null, grupo: 'Uso clínico' },
];

/* Fator para a unidade BASE de cada dimensão: volume em µl, massa em mcg. É o
   que permite declarar o conteúdo em ml e debitar em µl — um frasco de 30 ml
   são 30.000 µl, e é nessa escala que a aplicação é lançada. */
const DIMENSAO: Record<string, { dim: string; fator: number }> = {
    l: { dim: 'volume', fator: 1e6 },
    ml: { dim: 'volume', fator: 1e3 },
    ul: { dim: 'volume', fator: 1 },
    g: { dim: 'massa', fator: 1e6 },
    mg: { dim: 'massa', fator: 1e3 },
    mcg: { dim: 'massa', fator: 1 },
};

function acharUnidade(u: string | undefined): Unidade | undefined {
    if (!u) return undefined;
    return UNIDADES.find((x) => x.id === u || x.abrev === u);
}

/** Rótulo curto, sempre no singular. Aceita id ou a própria abreviação. */
export function unidadeAbrev(u: string | undefined): string {
    return acharUnidade(u)?.abrev ?? (u ?? '');
}

/**
 * A unidade concordando com a quantidade — use SEMPRE que ela aparecer ao lado
 * de um número. "3 frasco" é erro de português numa tela que a recepção lê o
 * dia inteiro, e é exatamente o que sai ao interpolar `unidadeAbrev` cru.
 *
 * Singular do zero (exclusive) ao dois (exclusive): "0,5 frasco" e "1,5
 * frasco", como manda a concordância com fração; zero e dois em diante vão no
 * plural ("0 frascos", "2,5 frascos"). O estoque desce em meia embalagem o
 * tempo todo, então a fração não é caso raro aqui.
 */
export function unidadeLabel(qtd: number, u: string | undefined): string {
    const achado = acharUnidade(u);
    if (!achado) return u ?? '';
    const n = Math.abs(qtd);
    if (!achado.plural || (n > 0 && n < 2)) return achado.abrev;
    return achado.plural;
}

/** Concorda um adjetivo: "1 tubo disponível" / "3 tubos disponíveis". */
export function concordar(qtd: number, singular: string, plural: string): string {
    return Math.abs(qtd) === 1 ? singular : plural;
}

/** Fator entre duas unidades. 1 quando não são conversíveis entre si. */
export function fatorConversao(de: string | undefined, para: string | undefined): number {
    if (!de || !para || de === para) return 1;
    const a = DIMENSAO[de];
    const b = DIMENSAO[para];
    if (!a || !b || a.dim !== b.dim) return 1;
    return a.fator / b.fator;
}

/** As unidades que fazem par com esta — mesma dimensão, mais ela mesma. */
export function unidadesCompativeis(u: string): string[] {
    const a = DIMENSAO[u];
    if (!a) return [u];
    return Object.keys(DIMENSAO).filter((k) => DIMENSAO[k]?.dim === a.dim);
}

/** Opções para o seletor, já agrupadas na ordem padrão. */
export function opcoesDeUnidade(): { value: string; label: string; grupo: string }[] {
    return UNIDADES.map((u) => ({ value: u.id, label: u.nome, grupo: u.grupo }));
}

/** O par de unidades de um produto — é só disto que a conta precisa. */
export interface MedidaDoProduto {
    /** A embalagem: como o estoque é contado. */
    unidade: string;
    /** Quanto vem em cada embalagem. */
    conteudo: number;
    /** Unidade em que o conteúdo é declarado (30 ml num frasco). */
    unidadeConteudo: string;
    /** Unidade em que o procedimento debita (µl, ml, UI). */
    unidadeConsumo: string;
}

/** Conteúdo da embalagem na unidade de CONSUMO. Frasco de 30 ml em µl → 30.000. */
export function conteudoEmConsumo(p: MedidaDoProduto): number {
    const conteudo = Number(p.conteudo) || 1;
    return conteudo * fatorConversao(p.unidadeConteudo, p.unidadeConsumo);
}

/** Custo por unidade de CONSUMO. Um frasco de 100 UI a R$ 890 dá R$ 8,90 por UI. */
export function custoDeConsumoCentavos(p: MedidaDoProduto & { custoCentavos: number }): number {
    return p.custoCentavos / (conteudoEmConsumo(p) || 1);
}

/** Quantas EMBALAGENS uma quantidade de consumo representa. */
export function embalagensDe(p: MedidaDoProduto, quantidadeConsumo: number): number {
    return (Number(quantidadeConsumo) || 0) / (conteudoEmConsumo(p) || 1);
}

/** Número com no máximo duas casas, sem zeros à toa: 0,5 frasco, 2 frascos. */
export function quantidade(valor: number): string {
    return valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

/** "0,5 frasco" — número e unidade já concordando. */
export function quantidadeComUnidade(valor: number, u: string | undefined): string {
    return `${quantidade(valor)} ${unidadeLabel(valor, u)}`;
}
